import { Injectable } from '@angular/core';

import { IField, IProfileEvent, ISearchProfileEventsResponse, ParticipantStageActionDenyType } from './shared/interfaces';
import { ApiService } from './shared/api.service';
import { EventType, NotificationMessageType } from './shared/enums';
import { IRowActionItem, ISortData, ITableSettings } from './table/interfaces';
import { DateConversion } from './enums';
import { MomentService } from './shared/moment.service';
import { IMyEventsData } from './interfaces';
import { SharedService } from './shared/shared.service';
import { NotificationsService } from './shared/notifications.service';

@Injectable({
    providedIn: 'root'
})
export class MyEventsService {
    constructor(
        private readonly apiSvc: ApiService,
        private readonly momentSvc: MomentService,
        private readonly sharedSvc: SharedService,
        private readonly notificationSvc: NotificationsService,
    ) {
    }

    async settingsChanged(
        settings: ITableSettings,
        myEventsData: IMyEventsData,
        language: string,
        dateTimeFormat: string,
        currentParticipantId: string,
        currentContactId: string,
        isCompanyManager: boolean,
        companyManagerDependencyResult: boolean,
        columns?: IField[]
    ): Promise<void> {
        myEventsData.lastTableSettings = settings;
        // Convert sort field by adding 'event.' prefix but only event fields should have it
        const clonedSettings = this.sharedSvc.clone(settings);
        this.addEventFieldsNamePrefixes(clonedSettings.sort);
        myEventsData.lastResponse = await this.searchEvents(clonedSettings, myEventsData.restrictToEventId);
        const eventsRes = this.setMyEventDatesByResponse(
            myEventsData.lastResponse,
            myEventsData.dateConversion,
            dateTimeFormat,
            language
        );
        myEventsData.dataSubject.next({
            rowObjects: eventsRes.events,
            pagedResponse: eventsRes,
            sortData: settings.sort,
            columns: columns
        });
    }

    getMyEventsTableColumns(): IField[] {
        const tableColumns: IField[] = [];
        tableColumns.push({ name: 'name', label: 'Name', schema: { type: 'string' } } as IField);
        tableColumns.push({ name: 'start', label: 'Start Date/Time', schema: { type: 'string', format: 'datetime' } } as IField);
        tableColumns.push({ name: 'end', label: 'End Date/Time', schema: { type: 'string', format: 'datetime' } } as IField);
        tableColumns.push({ name: 'crmstatus', label: 'Status', schema: { type: 'string' } } as IField);
        tableColumns.push({ name: 'first_name', label: 'First name', schema: { type: 'string' } } as IField);
        tableColumns.push({ name: 'last_name', label: 'Last name', schema: { type: 'string' } } as IField);
        tableColumns.push({ name: 'email', label: 'Email', schema: { type: 'string' } } as IField);
        return tableColumns;
    }

    async searchEvents(myEventsTableSettings: ITableSettings, eventId: string | null): Promise<ISearchProfileEventsResponse> {
        const eventsRes = await this.apiSvc.searchEvents(
            EventType.event,
            '',
            '',
            true,
            {
                limit: myEventsTableSettings.page.limit,
                offset: myEventsTableSettings.page.offset
            },
            myEventsTableSettings.sort.fieldName,
            myEventsTableSettings.sort.sortDirection,
            eventId
        );
        return eventsRes;
    }

    setMyEventDatesByResponse(
        response: ISearchProfileEventsResponse,
        dateConversion: DateConversion,
        dateTimeFormat: string,
        language: string,
    ): ISearchProfileEventsResponse {
        const eventsRes = this.sharedSvc.clone(response);
        this.setMyEventsDates(
            eventsRes.events,
            dateConversion,
            dateTimeFormat,
            language,
        );
        return eventsRes;
    }

    setMyEventsDates(
        events: IProfileEvent[],
        dateConversion: DateConversion,
        eventDateTimeFormat: string,
        language: string,
    ): void {
        for (const event of events) {
            if (dateConversion === DateConversion.objectTime) {
                event.start = this.momentSvc.formatDateTime(event.start, event.timezone, language, eventDateTimeFormat);
                event.end = this.momentSvc.formatDateTime(event.end, event.timezone, language, eventDateTimeFormat);
            } else if (dateConversion === DateConversion.myTime) {
                event.start = this.momentSvc.toLocalDateTime(event.start, language, eventDateTimeFormat);
                event.end = this.momentSvc.toLocalDateTime(event.end, language, eventDateTimeFormat);
            }
        }
    }

    isViewLinkVisible(event: IProfileEvent, currentParticipantId: string, currentContactId: string, isCompanyManager: boolean, companyManagerDependencyResult: boolean): boolean {
        const isMyLink = event.contact_id === currentContactId || event.participant_id === currentParticipantId;
        if (isMyLink) {
            return true;
        }

        const isOtherLink = event.contact_id !== currentContactId && event.participant_id !== currentParticipantId;
        if (isCompanyManager && companyManagerDependencyResult && isOtherLink) {
            return true;
        }

        return false;
    }

    createParticipantStageActionRowActions(
        profileItem: IProfileEvent,
        postActionFn: () => Promise<void>
    ): IRowActionItem<IProfileEvent>[] {
        const stageActionCallback = async (row: IProfileEvent, deny: ParticipantStageActionDenyType) => {
            await this.apiSvc.participantStageAction(
                row.participant_id,
                row.stage_id,
                deny,
            ).catch().then(async () => {
                await postActionFn();
                this.notificationSvc.showNotification(NotificationMessageType.success, this.sharedSvc.translate('PARTICIPANT_STAGE_UPDATED'));
            });
        };
        return profileItem.can_approve ? [
            {
                label: 'APPROVE',
                actionFn: (row) => stageActionCallback(row, ParticipantStageActionDenyType.Approve),
                key: 'approve'
            },
            {
                label: 'DENY',
                actionFn: (row) => stageActionCallback(row, ParticipantStageActionDenyType.Deny),
                key: 'deny'
            },
        ]: [];
    }

    addEventFieldsNamePrefixes(sort: ISortData): void {
        const eventFieldNames = ['name', 'start', 'end'];
        if (eventFieldNames.includes(sort?.fieldName)) {
            sort.fieldName = 'event.' + sort.fieldName;
        }
    }
}
